import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Stack,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { EditRounded, Cancel, AddCircleOutline, Launch } from '@mui/icons-material';
import { Instagram, Youtube, Facebook, Twitter, Music } from 'lucide-react';
import { IWorkProfile, IProfileSocials, TPlatform } from '../../types/global/helper';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import ThspianIcon from '../CustomSVGIcons/ThspianIcon';
import CustomButton from '../Button/CustomButton';

export interface ISocialMediaSectionProps {
    workProfile?: IWorkProfile;
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    previewMode?: boolean;
    onSubmitForm?: (data: any) => void;
}

const PLATFORMS: { value: TPlatform; label: string; icon: any; color: string }[] = [
    { value: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
    { value: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000' },
    { value: 'tiktok', label: 'TikTok', icon: Music, color: '#000000' },
    { value: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
    { value: 'twitter', label: 'Twitter', icon: Twitter, color: '#1DA1F2' },
    { value: 'thspian', label: 'Thspian', icon: ThspianIcon, color: '#E38627' },
];

const getPlatformIcon = (platform: TPlatform) => {
    const platformData = PLATFORMS.find(p => p.value === platform);
    return platformData?.icon || Instagram;
};

const getPlatformColor = (platform: TPlatform) => {
    const platformData = PLATFORMS.find(p => p.value === platform);
    return platformData?.color || '#E4405F';
};

const getPlatformLabel = (platform: TPlatform) => {
    const platformData = PLATFORMS.find(p => p.value === platform);
    return platformData?.label || platform;
};

const getSocialMediaUrl = (platform: TPlatform, username: string) => {
    switch (platform) {
        case 'instagram':
            return `https://www.instagram.com/${username}`;
        case 'youtube':
            return `https://www.youtube.com/@${username}`;
        case 'tiktok':
            return `https://www.tiktok.com/@${username}`;
        case 'facebook':
            return `https://www.facebook.com/${username}`;
        case 'twitter':
            return `https://www.twitter.com/${username}`;
        case 'thspian':
            return `https://thspian.com/${username}`;
        default:
            return '';
    }
};

const formatNumber = (num: number): string => {
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
};

export const SocialMediaSection: React.FC<ISocialMediaSectionProps> = ({
    workProfile,
    themeStyle,
    isEditMode = false,
    previewMode = false,
    onSubmitForm,
}) => {
    const [socials, setSocials] = useState<IProfileSocials[]>(workProfile?.socials || []);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newSocial, setNewSocial] = useState<IProfileSocials>({
        platform: 'instagram',
        bio: '',
        engagement: 0,
        followers: 0,
        following: 0,
        username: '',
    });

    useEffect(() => {
        setSocials(workProfile?.socials || []);
    }, [workProfile]);

    const handleSave = () => {
        if (onSubmitForm) {
            onSubmitForm({ socials });
        }
    };

    const handleAddSocial = () => {
        if (newSocial.bio && newSocial.followers > 0) {
            setSocials([...socials, newSocial]);
            setNewSocial({
                platform: 'instagram',
                bio: '',
                engagement: 0,
                followers: 0,
                following: 0,
                username: '',
            });
            setShowAddForm(false);
        }
    };

    const handleEditSocial = (index: number) => {
        setEditingIndex(index);
        setNewSocial(socials[index]);
    };

    const handleUpdateSocial = () => {
        if (editingIndex !== null && newSocial.bio && newSocial.followers > 0) {
            const updatedSocials = [...socials];
            updatedSocials[editingIndex] = newSocial;
            setSocials(updatedSocials);
            setEditingIndex(null);
            setNewSocial({
                platform: 'instagram',
                bio: '',
                engagement: 0,
                followers: 0,
                following: 0,
                username: '',
            });
        }
    };

    const handleDeleteSocial = (index: number) => {
        const updatedSocials = socials.filter((_, i) => i !== index);
        setSocials(updatedSocials);
    };

    const handleCancel = () => {
        setEditingIndex(null);
        setShowAddForm(false);
        setNewSocial({
            platform: 'instagram',
            bio: '',
            engagement: 0,
            followers: 0,
            following: 0,
            username: '',
        });
    };

    // Filter socials with values for display
    const socialsWithValues = socials.filter(social => social.bio && social.followers > 0);

    // Calculate aggregated metrics
    const totalReach = socialsWithValues.reduce((sum, social) => sum + social.followers, 0);
    const totalEngagement = socialsWithValues.reduce((sum, social) => sum + social.engagement, 0);
    const avgEngagementRate = socialsWithValues.length > 0 ? totalEngagement / socialsWithValues.length : 0;

    return (
        <Box sx={{ mb: 3 }}>
            {/* Section Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '18px',
                    }}>
                    Social Media
                </Typography>
                {!previewMode && (
                    <IconButton onClick={() => setShowAddForm(true)} sx={{ color: theme[themeStyle].highlight }}>
                        <AddCircleOutline />
                    </IconButton>
                )}
            </Box>

            {/* Aggregated Metrics */}
            {((isEditMode && socials.length > 0) || (!isEditMode && socialsWithValues.length > 0)) && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: theme[themeStyle].form_black,
                        borderRadius: '8px',
                        border: `1px solid ${theme[themeStyle].tag_white}`,
                    }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '16px',
                            mb: 1,
                        }}>
                        Total Reach & Engagement
                    </Typography>
                    <Stack direction="row" spacing={3}>
                        <Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].highlight,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '20px',
                                }}>
                                {formatNumber(totalReach)}
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].light_gray_text,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '12px',
                                }}>
                                Total Followers
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].highlight,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '20px',
                                }}>
                                {avgEngagementRate.toFixed(1)}%
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].light_gray_text,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '12px',
                                }}>
                                Avg Engagement
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            )}

            {/* Add/Edit Form */}
            {(showAddForm || editingIndex !== null) && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: theme[themeStyle].form_black,
                        borderRadius: '8px',
                        border: `1px solid ${theme[themeStyle].tag_white}`,
                    }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '16px',
                            mb: 2,
                        }}>
                        {editingIndex !== null ? 'Edit Social Media' : 'Add Social Media'}
                    </Typography>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Platform
                            </InputLabel>
                            <Select
                                value={newSocial.platform}
                                onChange={e => setNewSocial({ ...newSocial, platform: e.target.value as TPlatform })}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                {PLATFORMS.map(platform => (
                                    <MenuItem key={platform.value} value={platform.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <platform.icon size={16} style={{ color: platform.color }} />
                                            {platform.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Username"
                            value={newSocial.username}
                            onChange={e => setNewSocial({ ...newSocial, bio: e.target.value })}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <TextField
                            label="Followers"
                            type="number"
                            value={newSocial.followers}
                            onChange={e => setNewSocial({ ...newSocial, followers: parseInt(e.target.value) || 0 })}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <TextField
                            label="Engagement Rate (%)"
                            type="number"
                            value={newSocial.engagement}
                            onChange={e => setNewSocial({ ...newSocial, engagement: parseFloat(e.target.value) || 0 })}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                onClick={editingIndex !== null ? handleUpdateSocial : handleAddSocial}
                                sx={{
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].tag_white,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                {editingIndex !== null ? 'Update' : 'Add'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancel}
                                sx={{
                                    borderColor: theme[themeStyle].tag_white,
                                    color: theme[themeStyle].tag_white,
                                    '&:hover': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Social Media List */}
            <Stack spacing={2}>
                {(isEditMode ? socials : socialsWithValues).map((social, index) => (
                    <Paper
                        key={index}
                        elevation={0}
                        sx={{
                            p: 2,
                            backgroundColor: theme[themeStyle].form_black,
                            borderRadius: '8px',
                            border: `1px solid ${theme[themeStyle].tag_white}`,
                        }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {React.createElement(getPlatformIcon(social.platform), {
                                    size: 24,
                                    style: { color: getPlatformColor(social.platform) },
                                })}
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: '16px',
                                        }}>
                                        {getPlatformLabel(social.platform)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '14px',
                                        }}>
                                        @{social.username}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {!previewMode && (
                                    <IconButton
                                        onClick={() => handleEditSocial(index)}
                                        sx={{ color: theme[themeStyle].highlight }}>
                                        <EditRounded />
                                    </IconButton>
                                )}
                                {!previewMode && (
                                    <IconButton
                                        onClick={() => handleDeleteSocial(index)}
                                        sx={{ color: theme[themeStyle].secondary_red }}>
                                        <Cancel />
                                    </IconButton>
                                )}
                                <IconButton
                                    onClick={() =>
                                        window.open(getSocialMediaUrl(social.platform, social.username), '_blank')
                                    }
                                    sx={{ color: theme[themeStyle].highlight }}>
                                    <Launch />
                                </IconButton>
                            </Box>
                        </Box>
                        <Stack direction="row" spacing={3}>
                            <Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '18px',
                                    }}>
                                    {formatNumber(social.followers)}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        fontSize: '12px',
                                    }}>
                                    Followers
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '18px',
                                    }}>
                                    {social.engagement.toFixed(1)}%
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        fontSize: '12px',
                                    }}>
                                    Engagement
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            {/* Save Button */}
            {isEditMode && socials.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <CustomButton
                        label=" Save Social Media"
                        onClick={handleSave}
                        sx={{
                            '&:hover': {
                                WebkitTextFillColor: theme[themeStyle].primary_orange,
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};
